/**
 * ProductView — main entry point for Product domain.
 *
 * Renders product detail view with plan viewer, compartments, pricing, and related sections.
 * Layout and section visibility are driven by ProductContext (configured via panel settings).
 *
 * This component should be mounted within ProductContextProvider, typically from App.jsx
 * when SECTION_ID.PRODUCT is active.
 *
 * DEV MODE: Toggle diagnostic overlay (top-right button) to show capability/sections debug info.
 */

import { useState, useMemo, useCallback } from 'react'

import { useApp } from '../../../app/AppContext.jsx'
import { useCatalog } from '../../catalog/context/CatalogContext.jsx'
import { ProductContextProvider, useProduct } from '../index.js'

import { useI18n } from '../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../shared/i18n/useProductStrings.js'

import { ACTION_IDS, parseActiveActions } from '../../catalog/utils/unitTableSettings.js'
import { SECTION_ID } from '../../../shared/constants/sectionIds.js'

import { PRODUCT_VIEW_PANE, PRODUCT_VIEW_PANE_LABEL_KEYS } from '../constants/productPanes.js'

import { ArrowLeft, Heart } from '../../../shared/ui/icons/index.js'
import { Button, ButtonGroup } from '../../../shared/ui/Button/index.js'
import { Tooltip } from '../../../shared/ui/Tooltip/index.js'

import { ActionsPane } from './InfoPanes/ActionsPane.jsx'
import { CompartmentsPane } from './InfoPanes/CompartmentsPane.jsx'
import { DetailsPane } from './InfoPanes/DetailsPane.jsx'
import { DisclaimerPane } from './InfoPanes/DisclaimerPane.jsx'
import { InquiryPane } from './InfoPanes/InquiryPane.jsx'
import { ModeControllerPane } from './InfoPanes/ModeControllerPane.jsx'
import { ModeDetailsPane } from './InfoPanes/ModeDetailsPane.jsx'
import { PlanViewer } from './PlanViewer/PlanViewer.jsx'
import { PricingPane } from './InfoPanes/PricingPane.jsx'
import { ResourcesPane } from './InfoPanes/ResourcesPane.jsx'
import { SummaryPane } from './InfoPanes/SummaryPane.jsx'
import { ViewerSideControls } from './PlanViewer/ViewerSideControls/ViewerSideControls.jsx'

import styles from './ProductView.module.scss'

// ─────────────────────────────────────────────────────────────────────────────

function ProductHeading({
  product,
  previousProduct,
  nextProduct,
  navigationDisabledReason,
  showFavorites,
  isFavorited,
  onToggleFavorite,
  onSelectProduct,
}) {
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings();

  const productNumber = product?.id_original ?? product?.id_cad ?? '—'
  const productUuid = product?.uuid ?? null

  function handleSelectProduct(candidate) {
    const candidateUuid = candidate?.uuid ?? null
    if (!candidateUuid) return

    onSelectProduct?.(candidateUuid, {
      buildingId: candidate?.building_id ?? null,
      refTier: candidate?.ref_tier ?? candidate?.tiers?.[0]?.ref_tier ?? null,
    })
  }

  const navigationGroup = (
    <ButtonGroup
      size="large"
      className={styles.headingNavGroup}
      aria-label={t('product.navigation_aria')}
      tabIndex={navigationDisabledReason ? 0 : undefined}
    >
      <Button
        type="button"
        kind="icon"
        size="large"
        className={styles.headingNavButton}
        onClick={() => handleSelectProduct(previousProduct)}
        disabled={!previousProduct}
        aria-label={t('product.previous_aria')}
      >
        <ArrowLeft width="0.7em" height="1em" />
      </Button>
      <Button
        type="button"
        kind="icon"
        size="large"
        className={styles.headingNavButton}
        onClick={() => handleSelectProduct(nextProduct)}
        disabled={!nextProduct}
        aria-label={t('product.next_aria')}
      >
        <ArrowLeft className={styles.headingNavIconNext} width="0.7em" height="1em" />
      </Button>
    </ButtonGroup>
  )

  return (
    <div className={styles.headingBlock} data-product-heading>
      <div className={styles.headingRow}>
        <div className={styles.headingText}>
          <span className={styles.headingTitle}>
            {getProductsCategoryString('sheet', product.product_category)}
          </span>
        </div>

        <div className={styles.headingActions}>
          <Tooltip
            title={navigationDisabledReason}
            placement="bottom"
            disabled={!navigationDisabledReason}
          >
            {navigationGroup}
          </Tooltip>
        </div>
      </div>

      <div className={styles.headingRow}>
        <span className={styles.headingNumber}>{t('product.number_label', { number: productNumber })}</span>

        {showFavorites && (
          <Button
            type="button"
            kind="icon"
            size="large"
            selected={isFavorited}
            className={styles.headingFavoriteButton}
            onClick={onToggleFavorite}
            aria-label={isFavorited ? t('favorites.remove') : t('favorites.add')}
            aria-pressed={isFavorited}
            disabled={!productUuid}
          >
            <Heart filled={isFavorited} />
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Render pane by ID — dispatch to appropriate component
 */
function renderPane(paneId, isSticky, capabilities, modes, isMobile, zoomBridgeRef) {
  switch (paneId) {
    case PRODUCT_VIEW_PANE.HEADER:
      // Fixed in layout, not rendered here
      return null
    case PRODUCT_VIEW_PANE.VIEWER:
      return <><PlanViewer key={paneId} />{isMobile && <ViewerSideControls key={`${paneId}-controls`} isMobile={isMobile} zoomBridgeRef={zoomBridgeRef} />}</>
    case PRODUCT_VIEW_PANE.SUMMARY:
      return <SummaryPane key={paneId} />
    case PRODUCT_VIEW_PANE.PRICING:
      return <PricingPane key={paneId} />
    case PRODUCT_VIEW_PANE.INQUIRY:
      return <InquiryPane key={paneId} isSticky={isSticky} />
    case PRODUCT_VIEW_PANE.ACTIONS:
      return <ActionsPane key={paneId} />
    case PRODUCT_VIEW_PANE.COMPARTMENTS:
      return <CompartmentsPane key={paneId} />
    case PRODUCT_VIEW_PANE.DETAILS:
      return <DetailsPane key={paneId} />
    case PRODUCT_VIEW_PANE.MODE_DETAILS:
      return <ModeDetailsPane key={paneId} />
    case PRODUCT_VIEW_PANE.MODE_CONTROLLER:
      return <ModeControllerPane key={paneId} />
    case PRODUCT_VIEW_PANE.MAIN_DISCLAIMER:
      return <DisclaimerPane key={paneId} />
    case PRODUCT_VIEW_PANE.RESOURCES:
      return <ResourcesPane key={paneId} />
    default:
      return null
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function ProductViewInner() {
  const { t } = useI18n()
  const { 
    activateSectionHandler,
    projectConfig, 
    panelSettings, 
    isFavorite, 
    sectionRefs,
    selectProductHandler, 
    toggleFavorite,
    isMobile, 
  } = useApp()
  const { 
    filteredProducts, 
    products, 
    getAdjacentProducts, 
    productsStatus, 
    productsError,
    interiorsData, 
    interiorsError 
  } = useCatalog()
  const { 
    selection, 
    capabilities, 
    modes, 
    layout, 
    viewerState,
    status, 
    error, 
    zoomBridgeRef 
  } = useProduct()

  const [showDiagnostics, setShowDiagnostics] = useState(false)

  // ── Close handler ───────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    activateSectionHandler(SECTION_ID.HERO, { scrollTarget: SECTION_ID.TIERS })
  }, [activateSectionHandler])

  const activeActions = useMemo(() => parseActiveActions(panelSettings), [panelSettings])
  const showFavorites = activeActions.some((action) => action.id === ACTION_IDS.FAVORITES)

  const { previousProduct, nextProduct } = useMemo(() => {
    return getAdjacentProducts(selection.product)
  }, [getAdjacentProducts, selection.product])

  const currentProductUuid = selection.product?.uuid ?? null
  const hasVisibleCatalogProducts = filteredProducts.length > 0
  const isCurrentProductVisible = useMemo(() => {
    if (!currentProductUuid || filteredProducts.length === 0) return false
    return filteredProducts.some((product) => product?.uuid === currentProductUuid)
  }, [currentProductUuid, filteredProducts])
  const fallbackNavigationProduct = !isCurrentProductVisible && hasVisibleCatalogProducts
    ? filteredProducts[0] ?? null
    : null
  const previousNavigationProduct = previousProduct ?? fallbackNavigationProduct
  const nextNavigationProduct = nextProduct ?? fallbackNavigationProduct
  const navigationDisabledReason = !hasVisibleCatalogProducts
    ? t('product.navigation_disabled_filters')
    : null

  const isCurrentProductFavorite = selection.product?.uuid
    ? isFavorite?.(selection.product.uuid) === true
    : false

  const interiorCameraAssets = useMemo(() => {
    const tiers = Array.isArray(selection.product?.tiers) ? selection.product.tiers : []
    const byCamera = {}

    for (const tier of tiers) {
      const cameras = Array.isArray(tier?.cameras) ? tier.cameras : []
      for (const camera of cameras) {
        const name = String(camera?.name ?? '')
        if (!name.toLowerCase().startsWith('interior_')) continue
        if (byCamera[name]) continue

        const files = interiorsData?.[name]
        byCamera[name] = Array.isArray(files) ? files.length : 0
      }
    }

    return byCamera
  }, [selection.product, interiorsData])

  const diagnostics = useMemo(() => {
    const panelModes = panelSettings?.unit_sheet_modes ?? null
    const configuredModesList = Array.isArray(panelModes?.modes)
      ? panelModes.modes
      : Array.isArray(panelModes)
        ? panelModes
        : []

    return {
      selection: {
        productUuid: selection.product?.uuid ?? null,
        idCad: selection.product?.id_cad ?? null,
        idOriginal: selection.product?.id_original ?? null,
        productCategory: selection.productCategory ?? null,
        activeBuilding: selection.activeBuilding ?? null,
        activeTier: selection.activeTier ?? null,
      },
      fetchStatus: {
        productDomainStatus: status,
        catalogProductsStatus: productsStatus,
        productDomainError: error ? String(error) : null,
        catalogProductsError: productsError ? String(productsError) : null,
        interiorsError: interiorsError ? String(interiorsError) : null,
      },
      inputSnapshot: {
        projectConfig_pricesVisible: projectConfig?.website?.prices_visible ?? null,
        panelSettings_priceHistoryVisible:
          panelSettings?.additional_unit_information?.unit_price_history?.is_visible ?? null,
        panelSettings_supplementsVisible:
          panelSettings?.additional_unit_information?.supplement_purchases_options?.is_visible ?? null,
        panelSettings_resourcesVisible: panelSettings?.additional_resources?.is_visible ?? null,
        panelSettings_unitSheetModes: panelModes,
        panelSettings_unitSheetModesList: configuredModesList,
        datasetProductsCount: Array.isArray(products) ? products.length : 0,
        interiorsCameraKeyCount: interiorsData ? Object.keys(interiorsData).length : 0,
      },
      currentProductInteriors: {
        interiorCameraAssets,
        camerasWithAssetsCount: Object.values(interiorCameraAssets).filter((count) => count > 0).length,
      },
      capabilities,
      modes: {
        activeMode: modes.activeMode,
        defaultMode: modes.defaultMode,
        activeViewerVariant: modes.activeViewerVariant,
        availableViewerVariants: modes.availableViewerVariants,
        availableModes: modes.availableModes,
        activeColorScheme: viewerState.activeColorScheme,
        availableColorSchemes: capabilities.availableColorSchemes,
      },
      layout,
    }
  }, [
    capabilities,
    error,
    interiorCameraAssets,
    interiorsData,
    interiorsError,
    layout,
    modes,
    panelSettings,
    products,
    productsError,
    productsStatus,
    projectConfig,
    selection,
    status,
  ])

  // ── Computed layout ───────────────────────────────────────────────────────

  const desktopPaneOrder = layout?.desktopPaneOrder ?? []
  const mobilePaneOrder = layout?.mobilePaneOrder ?? []
  const stickyPanes = layout?.stickyPanes ?? new Set()

  // ── Render ────────────────────────────────────────────────────────────────

  if (!selection.product) {
    return (
      <section ref={sectionRefs.product} className={styles.productView} data-section={SECTION_ID.PRODUCT} aria-label={t('product.section_label')}>
        <div className={styles.empty}>
          <p>{t('product.no_product_selected')}</p>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section ref={sectionRefs.product} className={styles.productView} data-section={SECTION_ID.PRODUCT} aria-label={t('product.section_label')}>
        <div className={styles.error}>
          <p>{t('product.error_loading')}</p>
          <small>{error}</small>
        </div>
      </section>
    )
  }

  return (
    <section 
      ref={sectionRefs.product}
      className={styles.productView} 
      data-section={SECTION_ID.PRODUCT} 
      aria-label={t('product.section_label')}
    >
    {isMobile && (
    <div className={styles.mobileCanvas}>
          <ProductHeading
            product={selection.product}
            previousProduct={previousNavigationProduct}
            nextProduct={nextNavigationProduct}
            navigationDisabledReason={navigationDisabledReason}
            showFavorites={showFavorites}
            isFavorited={isCurrentProductFavorite}
            onToggleFavorite={() => {
              if (selection.product?.uuid) {
                toggleFavorite?.(selection.product.uuid)
              }
            }}
            onSelectProduct={selectProductHandler}
          />      {
      mobilePaneOrder
        .map((pane) => renderPane(pane.id, stickyPanes.has(pane.id), capabilities, modes, isMobile, zoomBridgeRef))
      }
    </div>
    )}
    { !isMobile && (
      <div className={styles.desktopCanvas}>
        <div className={styles.firstColumnWrapper}>
          <ProductHeading
            product={selection.product}
            previousProduct={previousNavigationProduct}
            nextProduct={nextNavigationProduct}
            navigationDisabledReason={navigationDisabledReason}
            showFavorites={showFavorites}
            isFavorited={isCurrentProductFavorite}
            onToggleFavorite={() => {
              if (selection.product?.uuid) {
                toggleFavorite?.(selection.product.uuid)
              }
            }}
            onSelectProduct={selectProductHandler}
          />
          <div className={styles.panesArea}>
            {desktopPaneOrder.map(
              (pane) => renderPane(
                pane.id, 
                stickyPanes.has(pane.id), 
                capabilities, 
                modes)
            )}
          </div>
        </div>
        <div className={styles.secondColumnWrapper}>
          <PlanViewer />
        </div>
        <div className={styles.thirdColumnWrapper}>
          <ViewerSideControls 
            isMobile={isMobile}
            zoomBridgeRef={zoomBridgeRef}
            onClose={handleClose}
          />
        </div>
      </div>
    )}
    </section>
  )
}

export function ProductView() {
  const { activeProduct, activeBuilding, activeTier, projectConfig, panelSettings } = useApp()
  const { products, interiorsData } = useCatalog()

  const product = products.find((item) => item.uuid === activeProduct || item.id_cad === activeProduct) ?? null
  const productCategory = product?.product_category ?? null

  return (
    <ProductContextProvider
      product={product}
      productCategory={productCategory}
      allProducts={products}
      interiorsData={interiorsData}
      activeBuilding={activeBuilding}
      activeTier={activeTier}
      projectConfig={projectConfig}
      panelSettings={panelSettings}
    >
      <ProductViewInner />
    </ProductContextProvider>
  )
}
